import React, { useEffect, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  callAPI,
  CREATE_PAYMENT_INTENT_ENDPOINT,
  VALIDATE_STRIPE_CUSTOMER_ENDPOINT,
} from "../../app/api/callAPI";
import { getCurrencyProperties } from "../data/currencies";
import { MAIN_COLOUR } from "../../css/styles";
import stripePower from "../../assets/icons/Powered-stripe-blurple.svg";
import lock from "../../assets/icons/lock.svg";
import { logErrorInFirestore } from "../../app/firestore/firestoreService";
import { Button, Icon } from "semantic-ui-react";
import { LOG_ERROR_CODES } from "../../app/firestore/firestoreConstants";

const STRIPE_CARD_NUMBER_ELEMENT = "cardNumber";
const STRIPE_CARD_EXPIRY_DATE_ELEMENT = "cardExpiry";
const STRIPE_CARD_CVC_ELEMENT = "cardCvc";

export default function StripeCustomCheckout({
  amount,
  resetTopUpPayment,
  account,
  paymentReason = "balance top-up",
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(
    account.stripeCustomerId
  );
  // const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false);
  const [isExpiryDateComplete, setIsExpiryDateComplete] = useState(false);
  const [isCardCvcComplete, setIsCardCvcComplete] = useState(false);

  useEffect(() => {
    if (stripeCustomerId || paymentError) return;

    async function getStripeCustomerId() {
      return callAPI({
        endpoint: VALIDATE_STRIPE_CUSTOMER_ENDPOINT,
      });
    }

    getStripeCustomerId()
      .then((customer) => setStripeCustomerId(customer.id))
      .catch(async (e) => {
        setPaymentError("Unable to top-up at the moment");
        await logErrorInFirestore({
          error: e.toString(),
          errorCode: LOG_ERROR_CODES.stripeCustomCheckout,
          notes: {
            userId: account.id,
            stripeCustomerId: stripeCustomerId,
            userEmail: account.email,
            currencyCode: account.currencyCode || "n/a",
            paymentReason: paymentReason,
            action: "Get stripe customer",
            paymentError: "Unable to top-up at the moment",
            endpoint: CREATE_PAYMENT_INTENT_ENDPOINT,
          },
        });
      });
  }, [
    account.currencyCode,
    account.email,
    account.id,
    paymentError,
    paymentReason,
    stripeCustomerId,
  ]);

  useEffect(() => {
    if (paymentError) return;
    if (amount && !clientSecret && stripeCustomerId) {
      const currencyCode = getCurrencyProperties(
        account.currencyCode
      ).code.toLowerCase();
      const body = {
        amount: amount,
        stripeCustomerId: stripeCustomerId,
        currency: currencyCode,
        description: "Employer account balance top-up",
        receipt_email: account.email,
      };

      async function customCheckout() {
        try {
          const { clientSecret, id } = await callAPI({
            endpoint: CREATE_PAYMENT_INTENT_ENDPOINT,
            opts: {
              body,
            },
          });

          return { clientSecret, id };
        } catch (e) {
          setPaymentError("Unable to top-up at the moment");
          await logErrorInFirestore({
            error: e.toString(),
            errorCode: LOG_ERROR_CODES.stripeCustomCheckout,
            notes: {
              userId: account.id,
              stripeCustomerId: stripeCustomerId,
              userEmail: account.email,
              currencyCode: account.currencyCode || "n/a",
              paymentReason: paymentReason,
              action: "Get client secret",
              paymentError: "Unable to top-up at the moment",
              endpoint: CREATE_PAYMENT_INTENT_ENDPOINT,
            },
          });
          return e;
        }
      }

      customCheckout().then(({ clientSecret, id }) => {
        setClientSecret(clientSecret);
        // setPaymentIntentId(id);
      });
    }
  }, [
    amount,
    account,
    clientSecret,
    stripeCustomerId,
    paymentReason,
    paymentError,
  ]);

  async function handlePaymentConfirmClick() {
    setIsProcessing(true);

    const payload = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
      },
    });

    setIsProcessing(false);

    if (payload.error) {
      setIsProcessing(false);
      setPaymentError(`Payment Failed: ${payload.error.message}`);
      setClientSecret(null);
      await logErrorInFirestore({
        error: payload.error.message,
        errorCode: LOG_ERROR_CODES.stripeCustomCheckout,
        notes: {
          userId: account.id,
          userEmail: account.email,
          stripeCustomerId: stripeCustomerId,
          currencyCode: account.currencyCode || "n/a",
          paymentReason: paymentReason,
          action: "Confirm payment",
        },
      });
    } else {
      // await something
      resetTopUpPayment();
    }
  }

  function handleCardDetailsChange(e) {
    const { error } = e;
    setError(error ? "Invalid card details" : "");

    if (e.elementType === STRIPE_CARD_NUMBER_ELEMENT) {
      setIsCardNumberComplete(e.complete);
    } else if (e.elementType === STRIPE_CARD_EXPIRY_DATE_ELEMENT) {
      setIsExpiryDateComplete(e.complete);
    } else if (e.elementType === STRIPE_CARD_CVC_ELEMENT) {
      setIsCardCvcComplete(e.complete);
    }
  }

  const inputStyle = {
    style: {
      base: {
        color: MAIN_COLOUR,
        fontFamily: "Lato, sans-serif",
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#a7a1a1",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  return (
    <div>
      <div className="medium-text" style={{ fontSize: 20 }}>
        Payment details
      </div>
      <div style={{ margin: "20px 0 22px", width: "85%" }}>
        <div className="body-blue-text" style={{ marginBottom: 10 }}>
          <span>Card information </span>
          <span style={{ float: "right" }}>
            <Icon name="cc amex" size="large" />
          </span>
          <span style={{ float: "right" }}>
            <Icon name="cc mastercard" size="large" />
          </span>
          <span style={{ float: "right" }}>
            <Icon name="cc visa" size="large" />
          </span>
        </div>
        <div className="payment-card-input-container">
          <div>
            <CardNumberElement
              onChange={handleCardDetailsChange}
              options={inputStyle}
              className="payment-card-input"
            />
          </div>
          <div>
            <CardExpiryElement
              onChange={handleCardDetailsChange}
              options={inputStyle}
              className="payment-card-input-expiry-date"
            />
          </div>
          <div>
            <CardCvcElement
              onChange={handleCardDetailsChange}
              options={inputStyle}
              className="payment-card-input"
            />
          </div>
        </div>
        {/*{error && (*/}
        {/*  <div className="absolute-position error-message">{error}</div>*/}
        {/*)}*/}
      </div>
      <Button
        className="overwrite-button cta-orange"
        content={
          <div className="center-flex">
            <img
              className="modal-confirm-tick-logo"
              src={lock}
              alt="lock"
              style={{ height: 19, width: 14, marginRight: 8 }}
            />
            {`Pay ${
              getCurrencyProperties(account.currencyCode).symbol
            }${amount.toFixed(2)}`}
          </div>
        }
        loading={isProcessing}
        disabled={
          error ||
          !clientSecret ||
          !stripeCustomerId ||
          !isCardNumberComplete ||
          !isExpiryDateComplete ||
          !isCardCvcComplete ||
          isProcessing
        }
        onClick={handlePaymentConfirmClick}
      />
      <div
        style={{ fontSize: 14, color: "#83898E", margin: "13px 0 16px 2px" }}
      >
        This will complete your top up payment. Your card will be charged once
        this payment is authorised.
      </div>
      <div>
        <img
          className="clickable"
          src={stripePower}
          style={{ paddingLeft: 2 }}
          // style={{ width: 17, height: 17 }}
          alt="arrow right"
          onClick={() => window.open("https://stripe.com/en-cy", "_blank")}
        />
      </div>
      {paymentError && (
        <div
          className="error-message absolute-position"
          style={{ marginTop: 2, fontSize: 16 }}
        >
          {paymentError}
        </div>
      )}
    </div>
  );
}
